import React, { useState, useContext } from "react";
import { Box, useTheme, TextField, Button, Typography } from "@mui/material";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { useColourMode } from "../contexts/ColourContext";

const AddExhibit = ({ userId, exhibitUrl, header }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [message, setMessage] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();
  const { isLoggedIn } = useContext(AuthContext);

  const [buttonStyle, setButtonStyle] = useState({
    backgroundColor: accentColour,
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const maxSize = 512 * 1024; // 512KB

    const compressImage = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const maxWidth = 800;
            const maxHeight = 800;
            let width = img.width;
            let height = img.height;

            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width *= ratio;
              height *= ratio;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  if (blob.size > maxSize) {
                    reject(new Error("Image is too large after compression"));
                  } else {
                    const compressedFile = new File([blob], file.name, {
                      type: "image/jpeg",
                      lastModified: Date.now(),
                    });
                    resolve(compressedFile);
                  }
                } else {
                  reject(new Error("Compression failed"));
                }
              },
              "image/jpeg",
              0.6
            );
          };

          img.onerror = () => reject(new Error("Failed to load image"));
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
      });
    };

    if (file.size > maxSize) {
      compressImage(file)
        .then((compressedFile) => {
          setImage(compressedFile);
          alert("Image was compressed and uploaded successfully.");
        })
        .catch((err) => {
          console.error("Image compression failed:", err);
          alert(
            "Image is too large. Please select an image smaller than 512KB."
          );
          e.target.value = "";
        });
    } else {
      setImage(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("accessLevel", isLoggedIn.accessLevel);
    if (image) {
      formData.append("exhibitImage", image);
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMessage("Authorization token is missing.");
        return;
      }

      const response = await axios.post(`${exhibitUrl}/${userId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status >= 200 && response.status < 300) {
        setMessage("EXHIBIT ADDED");
        setButtonStyle({ backgroundColor: "green" });
        clearForm();
        window.location.reload();
      } else {
        throw new Error("Failed");
      }
    } catch (error) {
      console.error("Error adding exhibit:", error);
      setMessage("FAILED. TRY LATER");
      setButtonStyle({ backgroundColor: "red" });
    }

    setTimeout(() => {
      setMessage("");
      setButtonDisabled(false);
      setButtonStyle({ backgroundColor: accentColour });
    }, 2000);
  };

  const clearForm = () => {
    setTitle("");
    setDescription("");
    setImage(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: "0.5rem" }}>
        <Typography variant="h6">{header}</Typography>
      </Box>
      <Box
        sx={{
          backdropFilter: "blur(1rem)",
          boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
          width: "100%",
          border: `1px solid ${colourTheme.palette.text.primary}`,
          borderRadius: "1rem",
          m: "auto",
          mb: 2.5,
          p: 1,
          height: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Title */}
          <Box sx={{ m: "0.5rem" }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: colourTheme.palette.text.primary,
                  },
                  "&:hover fieldset": {
                    borderColor: colourTheme.palette.text.primary,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: colourTheme.palette.text.primary,
                },
                "& .MuiInputBase-input": {
                  color: colourTheme.palette.text.primary,
                },
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ m: "0.5rem" }}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: colourTheme.palette.text.primary,
                  },
                  "&:hover fieldset": {
                    borderColor: colourTheme.palette.text.primary,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: colourTheme.palette.text.primary,
                },
                "& .MuiInputBase-input": {
                  color: colourTheme.palette.text.primary,
                },
              }}
            />
          </Box>

          {/* Image Upload */}
          <Box sx={{ m: "0.5rem" }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                borderColor: colourTheme.palette.text.primary,
                color: colourTheme.palette.text.primary,
                "&:hover": {
                  borderColor: colourTheme.palette.text.primary,
                },
              }}
            >
              Upload Image
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required
                hidden
              />
            </Button>
            {image && (
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                Selected: {image.name}
              </Typography>
            )}
          </Box>

          {/* Submit Button */}
          <Box
            sx={{ width: "100%", display: "grid", placeItems: "center", mt: 2 }}
          >
            <Button
              type="submit"
              disabled={buttonDisabled}
              variant="contained"
              sx={{
                width: "60%",
                padding: "0.75rem 1.5rem",
                fontSize: "0.9rem",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: accentColour,
                },
                ...buttonStyle,
              }}
            >
              {message || "Add Exhibit"}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default AddExhibit;
